import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { ResourceGrid, RTakeItFurther } from '../components/Resources.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  rKicker: 'Free Welsh resources',
  rTitle: 'Songs, hymns, lessons for kids, no sign-up needed.',
  rLede: 'Everything on this page is free and will always be free. We hope it helps you sing the anthem with real understanding, and maybe even share some Welsh with your kids at bedtime.\n\nIf you find it helpful, do please share it.',

  rStat1Num: '100+',
  rStat1Label: 'free audio and video',
  rStat2Num: '100%',
  rStat2Label: 'free, forever',
  rStat3Num: 'No',
  rStat3Label: 'sign-up required',

  rSongsKicker: "Music - it's a Welsh Thing",
  rSongsTitle: 'You already know both of these. Now you can understand them.',
  rSongsSub: "When you understand what you're singing, the whole experience changes. The next time you sing the anthem, in the stadium, knowing what every word means, will be one of the most electric experiences of your life.",

  rStoriesKicker: 'Bedtime Welsh',
  rStoriesTitle: 'For parents to listen to with younger kids.',
  rStoriesSub: "These short videos start from the very beginning, go very slowly, and have Aran's kids helping them along.",

  rTakeKicker: 'Ready for more?',
  rTakeTitle: "When you're ready to start speaking, we'll be ready to start helping you.",
  rTakeBody: 'These free resources are a lovely way in – but if you want to hold a real conversation, you need the SaySomethingin Method. Try three minutes, free, right now.',
  rTakeCta1: 'See pricing',
  rTakeCta2: 'Try a free sample',
  rTakeQuote: 'I felt that it was important to be able to talk to my granddaughters in their own — and my — language.',
  rTakeQuoteName: 'Nigel',
  rTakeQuoteRole: 'SSi Welsh learner',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['rKicker','rTitle','rLede','rStat1Num','rStat1Label','rStat2Num','rStat2Label','rStat3Num','rStat3Label'], long:['rTitle','rLede'] },
  { name: 'Songs section', keys: ['rSongsKicker','rSongsTitle','rSongsSub'], long:['rSongsSub'] },
  { name: 'Stories section', keys: ['rStoriesKicker','rStoriesTitle','rStoriesSub'], long:['rStoriesSub'] },
  { name: 'Take it further', keys: ['rTakeKicker','rTakeTitle','rTakeBody','rTakeCta1','rTakeCta2','rTakeQuote','rTakeQuoteName','rTakeQuoteRole'], long:['rTakeBody','rTakeQuote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

/* ---- index page hero, with stat row ---- */

function IndexHero({ t }) {
  const style = {
    wrap: { background:'var(--offWhite, #FDF5F5)', borderBottom:'1px solid var(--border)', padding:'80px 32px 72px' },
    inner: { maxWidth:1040, margin:'0 auto', textAlign:'center' },
    kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
    h1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:62, lineHeight:1.04, margin:'0 0 22px', letterSpacing:'-.02em', textWrap:'balance' },
    lede: { fontSize:19, lineHeight:1.55, color:'var(--grey)', margin:'0 auto 38px', maxWidth:'60ch', textWrap:'pretty' },
    stats: { display:'inline-flex', alignItems:'center', gap:36, padding:'18px 32px', background:'#fff', borderRadius:18, border:'1px solid var(--border)' },
    stat: { textAlign:'center', minWidth:110 },
    statNum: { fontFamily:'var(--font-display)', fontSize:32, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.01em' },
    statLabel: { fontSize:12, color:'var(--grey)', marginTop:6, letterSpacing:'.04em' },
    statSep: { width:1, height:40, background:'var(--border)' },
  };
  return (
    <section style={style.wrap}>
      <div style={style.inner}>
        <div style={style.kicker}>{t.rKicker}</div>
        <h1 style={style.h1}>{t.rTitle}</h1>
        {t.rLede.split(/\n\n+/).map((para, i) => (
          <p key={i} style={style.lede}>{para}</p>
        ))}
        <div style={style.stats}>
          <div style={style.stat}>
            <div style={style.statNum}>{t.rStat1Num}</div>
            <div style={style.statLabel}>{t.rStat1Label}</div>
          </div>
          <div style={style.statSep}/>
          <div style={style.stat}>
            <div style={style.statNum}>{t.rStat2Num}</div>
            <div style={style.statLabel}>{t.rStat2Label}</div>
          </div>
          <div style={style.statSep}/>
          <div style={style.stat}>
            <div style={style.statNum}>{t.rStat3Num}</div>
            <div style={style.statLabel}>{t.rStat3Label}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Lucide-style inline icons, drawn rather than emoji. */
const IconMusic = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
  </svg>
);
const IconBook = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/>
  </svg>
);
const IconMoon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

export default function WelshResources() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });

  const SECTIONS = [
    {
      kicker: tweaks.rSongsKicker,
      title:  tweaks.rSongsTitle,
      sub:    tweaks.rSongsSub,
      cols: 2,
      cards: [
        { badge:'Hymn',       icon:<IconMusic/>,  title:'Calon Lân',               blurb:"The unofficial second anthem of Wales – sung at every rugby international, and in more than one or two pubs when the game's over.", meta:'~60 min · Lyrics + audio',      href:'/welsh/calon-lan' },
        { badge:'Anthem',     icon:<IconBook/>,   title:'Hen Wlad Fy Nhadau',      blurb:"The Welsh national anthem. Learn the words, understand what they mean, and you'll look like Alun Wyn the next time you're in the stadium.",                  meta:'~120 min · Lyrics + audio',      href:'/welsh/anthem' },
      ],
    },
    {
      kicker: tweaks.rStoriesKicker,
      title:  tweaks.rStoriesTitle,
      sub:    tweaks.rStoriesSub,
      cols: 2,
      cards: [
        { badge:'YouTube videos', icon:<IconMoon/>, title:'Bedtime Welsh', blurb:"Aran went a little stir-crazy during the Covid lockdowns, and started forcing his kids to do light-hearted mini-lessons for other children. To this day, he claims that they loved it, but he wouldn't try the same thing now they're teenagers.", meta:'100+ episodes · ~3 min each', href:'/welsh/bedtime' },
      ],
    },
  ];

  return (
    <div data-screen-label="Welsh resources">
      <Header t={tweaks}/>
      <main>
        <IndexHero t={tweaks}/>
        <ResourceGrid sections={SECTIONS}/>
        <RTakeItFurther t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
